import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, Wrench, ImagePlus, X, CalendarPlus, FileText, MoreVertical, Heart, Ban, Trash2, User, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/hooks/useSEO";
import { useAppointments } from "@/hooks/useAppointments";
import type { AppointmentFormData } from "@/hooks/useAppointments";
import AppointmentForm from "@/components/AppointmentForm";
import AppointmentCard from "@/components/AppointmentCard";
import { useConversationQuotes } from "@/hooks/useQuotes";
import DevisForm from "@/components/DevisForm";
import DevisCard from "@/components/DevisCard";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { checkMessageContent } from "@/lib/messageFilter";
import { MESSAGE_IMAGE_ACCEPTED } from "@/lib/constants";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import ClientBottomNav from "@/components/ClientBottomNav";
import { useToggleFavoriteArtisan, useFavoriteArtisanIds } from "@/hooks/useFavoriteArtisans";
import { useConversations, type ConversationWithMeta } from "@/hooks/useConversations";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  message_type: string;
  attachments: any[];
  created_at: string;
  read_at: string | null;
  is_deleted: boolean;
  blocked_reason: string | null;
}

const Messagerie = () => {
  useSEO({ noindex: true });
  const { t, locale } = useLanguage("client");
  const lNavigate = useLocalizedNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const { conversations, loading: conversationsLoading, reload } = useConversations();
  const [selectedConvo, setSelectedConvo] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showDevisForm, setShowDevisForm] = useState(false);
  const [deleteConvoId, setDeleteConvoId] = useState<string | null>(null);
  const [blockUserTargetId, setBlockUserTargetId] = useState<string | null>(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const toggleFavorite = useToggleFavoriteArtisan();
  const { data: favoriteIds } = useFavoriteArtisanIds();

  const handleDeleteConversation = async () => {
    if (!deleteConvoId) return;
    const { error } = await supabase.from("conversations").delete().eq("id", deleteConvoId);
    if (error) {
      toast({ title: t("messagerie.toast_convo_del_error"), variant: "destructive" });
    } else {
      toast({ title: t("messagerie.toast_convo_deleted") });
      if (selectedConvo === deleteConvoId) setSelectedConvo(null);
    }
    setDeleteConvoId(null);
  };

  const handleBlockUser = async (blockedId: string) => {
    const { error } = await supabase.from("blocked_users").insert({ blocker_id: user!.id, blocked_id: blockedId } as any);
    if (error) {
      if (error.code === "23505") {
        toast({ title: t("messagerie.toast_already_blocked") });
      } else {
        toast({ title: t("messagerie.toast_block_error"), variant: "destructive" });
      }
    } else {
      toast({ title: t("messagerie.toast_blocked_title"), description: t("messagerie.toast_blocked_desc") });
    }
  };

  const {
    appointments,
    loading: aptLoading,
    createAppointment,
    updateAppointmentStatus,
    counterPropose,
    getActiveAppointment,
  } = useAppointments(selectedConvo);

  const {
    quotes,
    canCreateNewQuote,
    loading: quotesLoading,
    createQuote,
    acceptQuote,
    rejectQuote,
    cancelQuote,
    markQuoteViewed,
    getQuoteWithLineItems,
  } = useConversationQuotes(selectedConvo);

  const activeAppointment = getActiveAppointment();

  useEffect(() => {
    if (!authLoading && !user) lNavigate("/connexion");
  }, [user, authLoading, lNavigate]);

  useEffect(() => {
    const convoId = searchParams.get("conversation");
    if (convoId) setSelectedConvo(convoId);
    if (searchParams.get("openDevis") === "1") setShowDevisForm(true);
  }, [searchParams]);

  useEffect(() => {
    if (!selectedConvo || !user) return;

    setMessages([]);
    setMessagesLoading(true);

    const loadMessages = async () => {
      const { data } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", selectedConvo)
        .order("created_at", { ascending: true });

      if (data) setMessages(data as Message[]);
      setMessagesLoading(false);

      await supabase.rpc("mark_conversation_read", {
        _conversation_id: selectedConvo,
        _user_id: user.id,
      });
    };

    loadMessages();

    const channel = supabase
      .channel(`messages-${selectedConvo}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${selectedConvo}` },
        async (payload) => {
          const newMsg = payload.new as Message;
          setMessages((prev) => [...prev, newMsg]);
          if (newMsg.sender_id !== user.id) {
            await supabase.rpc("mark_conversation_read", {
              _conversation_id: selectedConvo,
              _user_id: user.id,
            });
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [selectedConvo, user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const uploadImages = async (): Promise<any[]> => {
    if (imageFiles.length === 0) return [];
    setUploading(true);
    const attachments: any[] = [];

    for (const file of imageFiles) {
      const ext = file.name.split(".").pop();
      const path = `${user!.id}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("message-attachments")
        .upload(path, file, { contentType: file.type });

      if (!error) {
        const { data: urlData } = supabase.storage
          .from("message-attachments")
          .getPublicUrl(path);
        attachments.push({ type: "image", url: urlData.publicUrl });
      }
    }

    setUploading(false);
    return attachments;
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && imageFiles.length === 0) || !selectedConvo || !user) return;

    if (newMessage.trim()) {
      const blockReason = checkMessageContent(newMessage);
      if (blockReason) {
        toast({
          title: t("messagerie.toast_blocked_content"),
          description: t("messagerie.toast_blocked_content_desc"),
          variant: "destructive",
        });
        return;
      }
    }

    setSending(true);

    try {
      let attachments: any[] = [];
      let messageType = "text";

      if (imageFiles.length > 0) {
        attachments = await uploadImages();
        messageType = newMessage.trim() ? "text" : "image";
      }

      const { error } = await supabase.from("messages").insert({
        conversation_id: selectedConvo,
        sender_id: user.id,
        content: newMessage.trim() || (attachments.length > 0 ? "" : ""),
        message_type: messageType,
        attachments,
      } as any);

      if (error) {
        toast({ title: t("messagerie.toast_send_error"), variant: "destructive" });
      } else {
        setNewMessage("");
        setImageFiles([]);
      }
    } catch {
      toast({ title: t("messagerie.toast_send_error"), variant: "destructive" });
    }

    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((f) =>
      (MESSAGE_IMAGE_ACCEPTED as readonly string[]).includes(f.type)
    );
    if (validFiles.length < files.length) {
      toast({ title: t("messagerie.image_format_error"), description: t("messagerie.image_format_desc"), variant: "destructive" });
    }
    setImageFiles((prev) => [...prev, ...validFiles].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const loc = locale === "ar" ? "ar-DZ" : "fr-FR";

    if (diffDays === 0) return date.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" });
    if (diffDays === 1) return t("messagerie.yesterday");
    if (diffDays < 7) return date.toLocaleDateString(loc, { weekday: "short" });
    return date.toLocaleDateString(loc, { day: "numeric", month: "short" });
  };

  if (authLoading) return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    </div>
  );

  const selectedConvoData = conversations.find((c) => c.id === selectedConvo);
  const isClientInConvo = selectedConvoData?.client_id === user?.id;

  const lastAptMsgId = new Map<string, string>();
  messages
    .filter((m) => !m.is_deleted && !m.blocked_reason && m.message_type === "appointment")
    .forEach((m) => {
      const aptId = (m.attachments as any[])?.[0]?.appointmentId;
      if (aptId) lastAptMsgId.set(aptId, m.id);
    });

  const lastDevisMsgId = new Map<string, string>();
  messages
    .filter((m) => !m.is_deleted && !m.blocked_reason && m.message_type === "devis")
    .forEach((m) => {
      const quoteId = (m.attachments as any[])?.[0]?.quoteId;
      if (quoteId) lastDevisMsgId.set(quoteId, m.id);
    });

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 container py-4 pb-20 md:pb-4">
        <div className="flex h-[calc(100vh-5rem)] md:h-[calc(100vh-8rem)] gap-0 rounded-xl border bg-card shadow-warm-md overflow-hidden">
          {/* Conversation list panel */}
          <div className={`w-full border-r border-border/60 md:w-80 md:block flex-shrink-0 flex flex-col ${selectedConvo ? "hidden" : "flex"}`}>
            <div className="border-b border-border/60 px-4 py-3.5 flex items-center gap-2">
              <h2 className="font-display text-base font-semibold flex items-center gap-2 flex-1">
                <MessageSquare className="h-4.5 w-4.5 text-primary shrink-0" aria-hidden="true" />
                {t("messagerie.title")}
              </h2>
              {totalUnread > 0 && (
                <Badge className="h-5 min-w-5 rounded-full bg-destructive px-1.5 text-[10px] text-destructive-foreground leading-none" aria-label={t("messagerie.unread_aria", { count: totalUnread })}>
                  {totalUnread}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
                onClick={() => reload()}
                disabled={conversationsLoading}
                aria-label={t("messagerie.refresh")}
              >
                <RefreshCw className={`h-4 w-4 ${conversationsLoading ? "animate-spin" : ""}`} aria-hidden="true" />
              </Button>
            </div>

            <ScrollArea className="flex-1">
              {conversationsLoading ? (
                <div className="space-y-1 p-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg p-3">
                      <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-3.5 w-28" />
                        <Skeleton className="h-3 w-44" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <MessageSquare className="h-5 w-5 text-muted-foreground/50" aria-hidden="true" />
                  </div>
                  <p className="text-sm font-medium">{t("messagerie.empty")}</p>
                  <p className="text-xs mt-1">{t("messagerie.empty_hint")}</p>
                </div>
              ) : (
                conversations.map((convo) => (
                  <button
                    key={convo.id}
                    onClick={() => setSelectedConvo(convo.id)}
                    className={`w-full border-b border-border/50 px-4 py-3.5 text-left transition-colors hover:bg-muted/40 ${
                      selectedConvo === convo.id
                        ? "bg-primary/8 border-l-2 border-l-primary"
                        : convo.unread_count > 0
                        ? "bg-primary/4"
                        : ""
                    }`}
                    aria-label={`${convo.other_name}${convo.unread_count > 0 ? ` — ${t("messagerie.unread_aria", { count: convo.unread_count })}` : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full gradient-hero-card border text-sm font-bold text-primary select-none">
                        {convo.other_name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-display text-sm truncate ${convo.unread_count > 0 ? "font-bold" : "font-semibold"}`}>
                          {convo.other_name}
                        </p>
                        {convo.annonce_title && (
                          <p className="text-xs text-primary/70 mt-0.5 truncate flex items-center gap-1">
                            <Wrench className="h-3 w-3 shrink-0" aria-hidden="true" />
                            {convo.annonce_title}
                          </p>
                        )}
                        {convo.last_message_preview && (
                          <p className={`text-xs mt-0.5 truncate leading-relaxed ${convo.unread_count > 0 ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                            {convo.last_message_preview}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {formatTime(convo.last_message_at || convo.updated_at)}
                        </span>
                        {convo.unread_count > 0 && (
                          <Badge className="h-5 min-w-5 rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground leading-none" aria-hidden="true">
                            {convo.unread_count}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </ScrollArea>
          </div>

          {/* Messages panel */}
          <div className={`flex flex-1 flex-col min-w-0 ${!selectedConvo ? "hidden md:flex" : "flex"}`}>
            {selectedConvo && selectedConvoData ? (
              <>
                {/* Conversation header */}
                <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden shrink-0 h-9 w-9"
                    onClick={() => setSelectedConvo(null)}
                    aria-label={t("messagerie.back_convos")}
                  >
                    <DirectionalArrow className="h-5 w-5 rotate-180" aria-hidden="true" />
                  </Button>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full gradient-hero-card border text-sm font-bold text-primary select-none" aria-hidden="true">
                    {selectedConvoData.other_name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div
                    className={`min-w-0 flex-1 ${isClientInConvo ? "cursor-pointer" : ""}`}
                    onClick={() => isClientInConvo && lNavigate(`/artisan/${selectedConvoData.other_id}`)}
                    role={isClientInConvo ? "button" : undefined}
                    tabIndex={isClientInConvo ? 0 : undefined}
                    onKeyDown={(e) => isClientInConvo && e.key === "Enter" && lNavigate(`/artisan/${selectedConvoData.other_id}`)}
                  >
                    <p className="font-display font-semibold text-sm truncate hover:text-primary transition-colors">
                      {selectedConvoData.other_name}
                    </p>
                    {selectedConvoData.annonce_title && (
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        <Wrench className="h-3 w-3 shrink-0" aria-hidden="true" />
                        {selectedConvoData.annonce_title}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="shrink-0 h-9 w-9" aria-label={t("messagerie.options")}>
                        <MoreVertical className="h-5 w-5" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {isClientInConvo && (
                        <>
                          <DropdownMenuItem onClick={() => lNavigate(`/artisan/${selectedConvoData.other_id}`)}>
                            <User className="h-4 w-4 me-2" aria-hidden="true" />
                            {t("messagerie.view_profile")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleFavorite.mutate(selectedConvoData.other_id)}>
                            <Heart className={`h-4 w-4 me-2 ${favoriteIds?.has(selectedConvoData.other_id) ? "fill-destructive text-destructive" : ""}`} aria-hidden="true" />
                            {favoriteIds?.has(selectedConvoData.other_id) ? t("messagerie.remove_fav") : t("messagerie.add_fav")}
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setBlockUserTargetId(selectedConvoData.other_id)} className="text-destructive focus:text-destructive">
                        <Ban className="h-4 w-4 me-2" aria-hidden="true" />
                        {t("messagerie.block_action")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteConvoId(selectedConvo!)} className="text-destructive focus:text-destructive">
                        <Trash2 className="h-4 w-4 me-2" aria-hidden="true" />
                        {t("messagerie.delete_convo")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Messages area */}
                <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef}>
                  <div className="space-y-2">
                    {messagesLoading ? (
                      <div className="space-y-3 py-4">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}>
                            <Skeleton className={`h-10 rounded-2xl ${i % 2 === 0 ? "w-48" : "w-64"}`} />
                          </div>
                        ))}
                      </div>
                    ) : messages.length === 0 && (
                      <div className="text-center py-10">
                        <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                          <MessageSquare className="h-4 w-4 text-muted-foreground/50" aria-hidden="true" />
                        </div>
                        <p className="text-sm text-muted-foreground">{t("messagerie.conversation_start")}</p>
                      </div>
                    )}
                    {messages
                      .filter((m) => !m.is_deleted && !m.blocked_reason)
                      .map((msg, i, filtered) => {
                        const isMine = msg.sender_id === user?.id;
                        const showDate =
                          i === 0 ||
                          new Date(msg.created_at).toDateString() !==
                            new Date(filtered[i - 1].created_at).toDateString();

                        return (
                          <div key={msg.id}>
                            {showDate && (
                              <div className="flex justify-center my-4">
                                <span className="rounded-full bg-muted px-3 py-1 text-[10px] text-muted-foreground">
                                  {new Date(msg.created_at).toLocaleDateString(
                                    locale === "ar" ? "ar-DZ" : "fr-FR",
                                    { day: "numeric", month: "long", year: "numeric" }
                                  )}
                                </span>
                              </div>
                            )}

                            {msg.message_type === "appointment" ? (
                              (() => {
                                const attData = (msg.attachments as any[])?.[0];
                                const aptId = attData?.appointmentId;
                                const apt = appointments.find((a) => a.id === aptId);
                                const isLastMsg = aptId && lastAptMsgId.get(aptId) === msg.id;
                                if (!apt || !isLastMsg) return (
                                  <div className="flex justify-center my-2">
                                    <span className="text-xs text-muted-foreground bg-muted rounded-full px-3 py-1">{msg.content}</span>
                                  </div>
                                );
                                return (
                                  <div className="flex justify-center my-3">
                                    <AppointmentCard
                                      appointment={apt}
                                      onAccept={(id) => updateAppointmentStatus(id, "accepted")}
                                      onDecline={(id) => updateAppointmentStatus(id, "declined")}
                                      onCancel={(id) => updateAppointmentStatus(id, "canceled")}
                                      onCounterPropose={(id, data) => counterPropose(id, data)}
                                      loading={aptLoading}
                                    />
                                  </div>
                                );
                              })()
                            ) : msg.message_type === "devis" ? (
                              (() => {
                                const attData = (msg.attachments as any[])?.[0];
                                const quoteId = attData?.quoteId;
                                const isLastMsg = quoteId && lastDevisMsgId.get(quoteId) === msg.id;
                                if (!quoteId || !isLastMsg) return (
                                  <div className="flex justify-center my-2">
                                    <span className="text-xs text-muted-foreground bg-muted rounded-full px-3 py-1">{msg.content}</span>
                                  </div>
                                );
                                const quote = getQuoteWithLineItems(quoteId);
                                if (!quote) return (
                                  <div className="flex justify-center my-2">
                                    <span className="text-xs text-muted-foreground bg-muted rounded-full px-3 py-1">{msg.content}</span>
                                  </div>
                                );
                                return (
                                  <div className="flex justify-center my-3">
                                    <DevisCard
                                      quote={quote}
                                      onAccept={acceptQuote}
                                      onReject={rejectQuote}
                                      onCancel={cancelQuote}
                                      onView={markQuoteViewed}
                                      isArtisan={!isClientInConvo}
                                      loading={quotesLoading}
                                    />
                                  </div>
                                );
                              })()
                            ) : (
                            <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                              <div
                                className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-warm-sm ${
                                  isMine
                                    ? "bg-primary text-primary-foreground rounded-br-sm"
                                    : "bg-secondary text-secondary-foreground rounded-bl-sm"
                                }`}
                              >
                                {msg.attachments && (msg.attachments as any[]).length > 0 && (msg.attachments as any[]).some((a: any) => a.type === "image") && (
                                  <div className="grid gap-1 mb-1">
                                    {(msg.attachments as any[]).filter((a: any) => a.type === "image").map((att: any, idx: number) => (
                                      <img
                                        key={idx}
                                        src={att.url}
                                        alt={t("messagerie.attachment_alt")}
                                        className="rounded-xl max-w-full max-h-60 object-cover cursor-pointer"
                                        onClick={() => setLightboxUrl(att.url)}
                                      />
                                    ))}
                                  </div>
                                )}
                                {msg.content && (
                                  <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                                )}
                                <p
                                  className={`text-[10px] mt-1 text-right ${
                                    isMine ? "text-primary-foreground/55" : "text-muted-foreground"
                                  }`}
                                >
                                  {new Date(msg.created_at).toLocaleTimeString(
                                    locale === "ar" ? "ar-DZ" : "fr-FR",
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}
                                  {isMine && msg.read_at && " ✓✓"}
                                </p>
                              </div>
                            </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </ScrollArea>

                {showAppointmentForm && (
                  <div className="border-t border-border/60 bg-muted/20 overflow-y-auto max-h-[55vh]">
                    <div className="px-4 py-3">
                      <AppointmentForm
                        onSubmit={async (data: AppointmentFormData) => {
                          await createAppointment(data);
                          setShowAppointmentForm(false);
                        }}
                        onCancel={() => setShowAppointmentForm(false)}
                        loading={aptLoading}
                      />
                    </div>
                  </div>
                )}

                {showDevisForm && (
                  <div className="border-t border-border/60 bg-muted/20 overflow-y-auto max-h-[65vh]">
                    <div className="px-4 py-3">
                      <DevisForm
                        onSubmit={createQuote}
                        onClose={() => setShowDevisForm(false)}
                        loading={quotesLoading}
                      />
                    </div>
                  </div>
                )}

                {imageFiles.length > 0 && (
                  <div className="border-t border-border/60 px-4 py-2 flex gap-2 flex-wrap bg-muted/20">
                    {imageFiles.map((file, i) => (
                      <div key={i} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={t("messagerie.attachment_alt")}
                          className="h-16 w-16 rounded-lg object-cover border shadow-warm-sm"
                        />
                        <button
                          onClick={() => setImageFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          className="absolute -top-1.5 -right-1.5 rounded-full bg-destructive text-destructive-foreground h-5 w-5 flex items-center justify-center shadow-warm-sm"
                          aria-label={t("messagerie.delete_image_aria")}
                        >
                          <X className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Compose bar */}
                <div className="border-t border-border/60 p-3 bg-card">
                  <div className="flex gap-2 items-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-10 w-10 text-muted-foreground hover:text-foreground"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label={t("messagerie.attach_image")}
                    >
                      <ImagePlus className="h-5 w-5" aria-hidden="true" />
                    </Button>
                    {!isClientInConvo && (
                    <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`shrink-0 h-10 w-10 ${showAppointmentForm ? "text-primary" : activeAppointment ? "text-muted-foreground/40 cursor-not-allowed" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => !activeAppointment && setShowAppointmentForm(!showAppointmentForm)}
                      disabled={!!activeAppointment}
                      aria-label={activeAppointment ? t("messagerie.rdv_active") : t("messagerie.rdv_propose")}
                      aria-pressed={showAppointmentForm}
                      title={activeAppointment ? t("messagerie.rdv_active") : t("messagerie.rdv_propose")}
                    >
                      <CalendarPlus className="h-5 w-5" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`shrink-0 h-10 w-10 ${showDevisForm ? "text-primary" : !canCreateNewQuote ? "text-muted-foreground/40 cursor-not-allowed" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => canCreateNewQuote && setShowDevisForm(!showDevisForm)}
                      disabled={!canCreateNewQuote}
                      aria-label={canCreateNewQuote ? "Créer un devis" : "Un devis est déjà en attente"}
                      aria-pressed={showDevisForm}
                      title={canCreateNewQuote ? "Créer un devis" : "Un devis est déjà en attente"}
                    >
                      <FileText className="h-5 w-5" aria-hidden="true" />
                    </Button>
                    </>
                    )}
                    <Input
                      placeholder={t("messagerie.placeholder")}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="flex-1 h-10 bg-background"
                      aria-label={t("messagerie.send_label")}
                    />
                    <Button
                      variant="hero"
                      size="icon"
                      className="shrink-0 h-10 w-10"
                      onClick={handleSend}
                      disabled={sending || uploading || (!newMessage.trim() && imageFiles.length === 0)}
                      aria-label={t("messagerie.send_label")}
                    >
                      <Send className="h-4.5 w-4.5" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center px-4">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full gradient-hero-subtle border">
                    <MessageSquare className="h-7 w-7 text-primary/60" aria-hidden="true" />
                  </div>
                  <p className="font-display font-semibold text-foreground">{t("messagerie.your_messages")}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t("messagerie.select_convo")}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <ArtisanBottomNav />
      <ClientBottomNav />

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label={t("messagerie.lightbox_aria")}
        >
          <button
            className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
            onClick={() => setLightboxUrl(null)}
            aria-label={t("messagerie.lightbox_close")}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightboxUrl}
            alt={t("messagerie.attachment_alt")}
            className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <AlertDialog open={!!deleteConvoId} onOpenChange={(open) => !open && setDeleteConvoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("messagerie.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("messagerie.delete_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("messagerie.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConversation} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("messagerie.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!blockUserTargetId} onOpenChange={(open) => !open && setBlockUserTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("messagerie.block_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("messagerie.block_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("messagerie.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (blockUserTargetId) handleBlockUser(blockUserTargetId); setBlockUserTargetId(null); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("messagerie.block_action")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Messagerie;
